import type { Hotkey } from "@tanstack/react-hotkeys"
import { formatForDisplay } from "@tanstack/react-hotkeys"
import { Fragment, type PropsWithChildren } from "react"

import { Tooltip } from "~/components/reusable/Tooltip"

type Props = {
  label: string
  /** Rendered as a key-combo chip after the label. Omit for a label-only tooltip in the same style. */
  shortcut?: Hotkey
  /** Rendered as chips separated by "then" (e.g. G then H). Takes precedence over `shortcut`. */
  shortcutSequence?: readonly Hotkey[]
  side?: "top" | "right" | "bottom" | "left"
  align?: "start" | "center" | "end"
  disabled?: boolean
  disableHoverableContent?: boolean
  delayDuration?: number
}

function ShortcutChip({ hotkey }: { hotkey: Hotkey }) {
  return (
    <span className="bg-medium rounded-[6px] text-fg-muted label-sm px-1.5 py-0.5">
      {formatForDisplay(hotkey)}
    </span>
  )
}

export function ShortcutTooltip({
  label,
  shortcut,
  shortcutSequence,
  side = "bottom",
  align,
  disabled,
  disableHoverableContent,
  delayDuration,
  children,
}: PropsWithChildren<Props>) {
  return (
    <Tooltip
      side={side}
      align={align}
      disabled={disabled}
      disableHoverableContent={disableHoverableContent}
      delayDuration={delayDuration}
      className="px-2 py-1.5"
      body={
        <div className="flex items-center gap-2">
          <span>{label}</span>
          {shortcutSequence ? (
            <span className="flex items-center gap-1">
              {shortcutSequence.map((key, index) => (
                <Fragment key={index}>
                  {index > 0 && <span className="text-fg-muted label-sm">then</span>}
                  <ShortcutChip hotkey={key} />
                </Fragment>
              ))}
            </span>
          ) : (
            shortcut && <ShortcutChip hotkey={shortcut} />
          )}
        </div>
      }
    >
      {children}
    </Tooltip>
  )
}
